/**
 * Centralised RBAC helper.
 *
 * Roles (canonical since Iteration 31):
 *   - "admin"        Full access.
 *   - "soc_analyst"  View + run manual scans + manage automated scanners
 *                    + update findings + manage alert rules + download reports.
 *   - "read_only"    View dashboards/reports only. Cannot download PDF/CSV,
 *                    cannot manage users, cannot mutate any operational data.
 *
 * Legacy roles are aliased to their canonical form when reading `user.role`,
 * so pre-existing DB users continue to work while we migrate.
 */
import { useAuth } from "./auth";

const LEGACY = {
  super_admin: "admin",
  tenant_admin: "admin",
  security_manager: "admin",
  cbxadmin: "admin",
  consultant: "soc_analyst",
  security_analyst: "soc_analyst",
  executive_viewer: "read_only",
  auditor: "read_only",
};

export function canonicalRole(raw) {
  if (!raw) return "read_only";
  if (raw === "admin" || raw === "soc_analyst" || raw === "read_only" || raw === "client") return raw;
  return LEGACY[raw] || "read_only";
}

// Canonical action → set of roles allowed
const MATRIX = {
  // ---- user & tenant admin ------------------------------------------
  "users.manage":         ["admin"],
  "audit.view":           ["admin"],
  "anomalies.view":       ["admin"],
  "settings.manage":      ["admin"],   // email / shodan / branding / digest
  // ---- clients ------------------------------------------------------
  "client.create":        ["admin"],
  "client.edit":          ["admin"],
  "client.delete":        ["admin"],
  "client.branding":      ["admin"],
  // ---- scans & findings (operational) -------------------------------
  "scan.run":             ["admin", "soc_analyst"],
  "schedule.manage":      ["admin", "soc_analyst"],
  "finding.update":       ["admin", "soc_analyst"],
  "alert_rules.manage":   ["admin", "soc_analyst"],
  // ---- reporting ----------------------------------------------------
  "report.download":      ["admin", "soc_analyst", "client"],
  // ---- always allowed ----------------------------------------------
  "view":                 ["admin", "soc_analyst", "read_only", "client"],
  "ai.ask":               ["admin", "soc_analyst", "read_only", "client"],
};

export function canWithRole(role, action) {
  const r = canonicalRole(role);
  const allowed = MATRIX[action];
  if (!allowed) return false;
  return allowed.includes(r);
}

/** Hook: returns an object `{ role, can(action), is, organizationId }` for the current user. */
export function usePermissions() {
  const { user } = useAuth();
  const role = canonicalRole(user?.role);
  return {
    role,
    isAdmin: role === "admin",
    isSocAnalyst: role === "soc_analyst",
    isReadOnly: role === "read_only",
    isClient: role === "client",
    organizationId: user?.organization_id || null,
    can: (action) => canWithRole(role, action),
  };
}

/** Human-readable role labels (Spanish) for UI. */
export const ROLE_LABELS = {
  admin: "Administrador",
  soc_analyst: "Analista SOC",
  read_only: "Solo lectura",
  client: "Cliente",
};
